/**
 * PanelSpec, as the browser receives it.
 *
 * The server's parser (`workbench/server/panels/manifest.py`) is the one reader
 * of a declaration: it refuses what the shell cannot draw, naming the field
 * and saying why, and what reaches this page is its projection (`wire.py`) --
 * field names verbatim, enums as their value, tuples as arrays. So nothing
 * here refuses anything; these are the types the shell draws with. The word
 * lists come from the server's own, written into `./vocabulary.ts` by
 * `python -B -m workbench.server.panels.vocabulary`.
 */

import {
  ACTION_KINDS,
  AUDIENCES,
  BLOCK_KINDS,
  DEVELOPER_AUDIENCE,
  FIELD_TYPES,
  FORMATS,
  OWNER_AUDIENCE,
  RECORD_STATES,
  SLOTS,
  WAKES,
} from "./vocabulary";

export { OWNER_AUDIENCE };

export type Slot = (typeof SLOTS)[number];

/**
 * Who a page is for (CHARTER §7.183).
 *
 * `slot` says where a panel mounts, never who may open it, so an Owner could
 * type any panel's address and land on a developer's form or a registry dump
 * (D786 / D840). The declaration answers it, and `developer` is what a panel
 * that did not say means -- `declaredAudience` below is where that default
 * lives, so no caller decides it a second time.
 */
export type Audience = (typeof AUDIENCES)[number];

/**
 * What the shell draws, and the one it hosts. `surface` is CHARTER §7.130: a
 * page the plugin ships, put in a sandboxed iframe by `SurfaceHost` rather
 * than drawn by a renderer here.
 */
export type BlockKind = (typeof BLOCK_KINDS)[number];

export type ActionKind = (typeof ACTION_KINDS)[number];

/** When an action wakes the Agent (`liquid-page-runtime.md` §2). */
export type Wake = (typeof WAKES)[number];

export type FieldType = (typeof FIELD_TYPES)[number];

export type Format = (typeof FORMATS)[number];

/**
 * Where one claimed tool call stands, as the conversation reads it: `running`
 * until it answers, then `done`, `failed` or `unknown`; a call that handed its
 * work to a child stands where the child does -- `working`, `stopping`, then `done`,
 * `failed` or `stopped`. The keys of `PanelSpec.states`.
 */
export type RecordState = (typeof RECORD_STATES)[number];

export interface LedgerFilter {
  kinds?: string[];
  ref_prefixes?: string[];
  sessions?: string[];
  principal_ids?: string[];
  plugins?: string[];
  limit?: number;
  follow?: boolean;
}

export interface SurfaceBinding { panel: string; export: string }
/** A read's `version` is where its version sits in the answer; "" = it has none. */
export type PanelSource = { filter: LedgerFilter }
  | { tool: string; arguments?: Record<string, unknown>; version?: string }
  | { binding: SurfaceBinding; arguments?: Record<string, unknown>; version?: string };

export interface PanelAction {
  key: string;
  label: string;
  kind?: ActionKind;
  tool?: string | null;
  binding?: SurfaceBinding | null;
  /** Field keys whose values are sent as the tool's arguments. */
  argument_fields?: string[];
  /** Arguments the button carries itself, which nobody types. */
  constants?: Record<string, unknown>;
  /** Send the collected values as one argument under this name. */
  pack_fields_as?: string;
  /** Whether to ask first. The shell writes the question. */
  confirm?: boolean;
  metadata?: Record<string, unknown>;
  /** When this action wakes the Agent; absent means `none`. */
  wake?: Wake;
  /** Which of the block's reads it changes, by index. Empty means all of them. */
  refresh?: number[];
  /** Bound by the server to the original call, never taken from a hidden input. */
  record_arguments?: Record<string, string>;
  record_states?: RecordState[];
}

export interface PanelColumn {
  key: string;
  label: string;
  format?: Format;
  width?: number;
}

export interface PanelField {
  key: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  choices?: string[];
  choice_labels?: Record<string, string>;
  /** `choice` only: besides the listed answers, the person may write one of their own in the same field. */
  writable?: boolean;
  advanced?: boolean;
  placeholder?: string;
  default?: unknown;
  /**
   * Write-only, as `PanelField.secret` has it: a flag on a field rather than a
   * type of its own, because a secret is still a string and it is the handling
   * that differs. Never populated from stored data, never read back out.
   */
  secret?: boolean;
  minimum?: number | null;
  maximum?: number | null;
  pattern?: string;
  /** `type: "list"` only: the fields of one row. */
  item_fields?: PanelField[];
  /** A field of a row that is not drawn but travels with the row (a table's fixed row id). */
  hidden?: boolean;
}

export interface PanelBlock {
  kind: BlockKind;
  title?: string;
  columns?: PanelColumn[];
  fields?: PanelField[];
  actions?: PanelAction[];
  /** Which of the panel's `reads` feeds this block, by index. */
  source_index?: number;
  /**
   * Where to look, or what to say. A dotted path into the source row for
   * table / kv / card / markdown; on a markdown block with no such path in the
   * data, the body is the text itself.
   */
  body?: string;
  /** Explicit markdown binding. Missing data stays empty rather than displaying the path. */
  body_path?: string;
  empty_text?: string;
  /**
   * `surface` only: what the sandboxed page may read. On the block and not on
   * the panel because the sandbox is per block -- `surface.tail`'s filter is
   * pinned to these on the server, and what the page says about itself counts
   * for nothing.
   */
  reads?: PanelSource[];
  /** `surface` only: directories under the plugin's own data directory whose
   *  files the page may load as media. */
  media_roots?: string[];
}

export interface PanelSpec {
  key: string;
  plugin?: string;
  title: string;
  slot?: Slot;
  /** Who may open it. Absent means `developer` -- read it with
   *  `declaredAudience`, never by defaulting at the call site. */
  audience?: Audience;
  icon?: string;
  order?: number;
  reads?: PanelSource[];
  blocks: PanelBlock[];
  actions?: PanelAction[];
  /** RECORD slot: which ledger kinds, or which of its own tools, this panel
   *  claims -- `l0.effect.released`, `plugin.` (a prefix), `tool:plugin.run`. */
  renders?: string[];
  /** RECORD slot: the card's one line per state -- a phrase, or phrases tried
   *  in order. `panel/records.tsx` says how one is chosen. */
  states?: Partial<Record<RecordState, string | string[]>>;
  metadata?: Record<string, unknown>;
}

/**
 * Who this panel said it is for, with the default in one place.
 *
 * One function, because the default *is* the contract: a panel that has not
 * said is a developer's page, and a caller that wrote `?? "owner"` or checked
 * `!== "developer"` would be deciding that again, differently. Both doors into
 * a plugin panel ask this one -- the page the shell draws (`PanelView`) and the
 * address an owner typed (`App.tsx`).
 */
export function declaredAudience(spec: { audience?: string } | null | undefined): Audience {
  return spec?.audience === OWNER_AUDIENCE ? OWNER_AUDIENCE : DEVELOPER_AUDIENCE;
}

function isDict(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Read a dotted path out of a row: `layers`, `payload.approval.title`. */
export function pluck(row: unknown, key: string): unknown {
  let current: unknown = row;
  for (const part of key.split(".")) {
    if (isDict(current)) current = current[part];
    else if (Array.isArray(current) && /^\d+$/.test(part)) current = current[Number(part)];
    else return undefined;
  }
  return current;
}
